<template>
  <div class="activity-details">
    <div class="row">
      <div class="col-5 activity-list pad">
        <mq-layout mq="md+" class="hint">
          <p>{{ $t('back-hint') }}</p>
        </mq-layout>
        <div class="headline">
          <p>
            {{
                $t('value-activity-for-year', {
                  activity: activity.title[$i18n.locale],
                  year: $config.year
                })
            }}
          </p>
          <h3>
            <NumberTween :value="activity.value" auto :options="{ delay: 0.75 }" />
          </h3>
          <mq-layout mq="md+">
            <p>{{ $t('value-activity-cta') }}</p>
          </mq-layout>
        </div>
        <mq-layout mq="md+">
          <ul class="projects">
            <li class="project" v-for="(project, index) in projects" :key="index" @click="openProject(project)"
              @mouseover="highlight(project)" @mouseleave="highlight(null)" :class="{
                active: !highlighted || (highlighted && highlighted.id === project.id)
              }">
              <div :style="palette(project)" class="block" />
              <h5>{{ project.title[$i18n.locale] }}</h5>
            </li>
          </ul>
        </mq-layout>
        <p class="activity-notes" v-if="activity.notes">{{ activity.notes[$i18n.locale] }}</p>
      </div>
      <div class="col-5 graph-container">
        <AreaGraph :data="projects" progressive ref="graph" :holes="2" :highlighted="highlighted" @highlight="highlight"
          @select="openProject" />
      </div>
      <mq-layout :mq="['xsm', 'sm']" class="tab">
        <p v-if="!project">{{ $t('cta-open-project') }}</p>
        <p v-else class="selected-project" :style="{ color: project.palette.background }" @click="goToProject(project)">
          {{ project.title[$i18n.locale] }}
        </p>
      </mq-layout>
    </div>
    <Aside full :active="showAside" @close="closeProject" nested>
      <ProjectDetail :project="project" v-if="project" />
    </Aside>
  </div>
</template>

<script>
import Aside from '@/components/common/Aside';
import NumberTween from '@/components/common/NumberTween';
import ProjectDetail from './ProjectDetail';
import AreaGraph from '@/components/common/graphs/AreaGraph';

export default {
  name: 'ActivityDetails',
  components: {
    Aside,
    NumberTween,
    ProjectDetail,
    AreaGraph
  },
  props: {
    activity: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      project: null,
      showAside: false,
      highlighted: null
    };
  },
  mounted() {
    this.$refs.graph.next();
    if (this.$route.params.id !== undefined) {
      const project = this.projects.find(p => p.id === Number(this.$route.params.id));
      this.openProject(project, false);
    }
  },
  watch: {
    $route(to) {
      if (!to.name.includes('project-id')) {
        this.closeProject(false);
      }
    }
  },
  computed: {
    projects() {
      return this.activity.items.map(i => {
        return {
          ...i,
          color: i.palette.background,
          label: this.$options.filters.currencyShort(i.value)
        };
      });
    },
    palette() {
      return project => {
        return {
          backgroundColor: project.palette.background
        };
      };
    }
  },
  methods: {
    openProject(project, nav) {
      this.project = project;
      if (!this.$device.mobile) this.goToProject(project, nav);
    },
    goToProject(project, nav) {
      this.showAside = true;
      if (nav !== false) this.$router.push({ path: project.id.toString(), append: true });
    },
    closeProject(nav = true) {
      this.showAside = false;
      if (nav) {
        const parent = this.$route.matched[this.$route.matched.length - 2];
        if (parent) this.$router.push(parent.path);
      }
    },
    highlight(project) {
      this.highlighted = project;
    }
  }
};
</script>

<style lang="scss" scoped>
.activity-details {
  height: 100%;
  overflow: hidden;

  .row {
    height: 100%;

    .activity-list {
      display: flex;
      flex-direction: column;

      @media screen and (max-width: $mq-tablet) {
        height: auto;
      }

      .hint {
        margin-bottom: $padding/2;
      }

      @media screen and (max-height: 750px) {
        margin-bottom: $padding/4;
      }

      .headline {
        margin-bottom: $padding/4;

        @media screen and (max-width: $mq-tablet) {
          margin-bottom: 0;
        }

        h3 {
          margin-bottom: $padding/6;

          @media screen and (max-width: $mq-tablet) {
            margin-bottom: 0;
          }
        }

        p {
          margin-bottom: 0.5rem;
        }
      }

      .projects {
        list-style-type: none;

        li {
          display: flex;
          flex-direction: row;
          align-items: center;
          margin-bottom: 1.5rem;
          cursor: pointer;
          transition: opacity 0.3s;
          opacity: 0.3;

          @media screen and (max-height: 750px) {
            margin-bottom: 0.75rem;
          }

          &.active {
            opacity: 1;
          }

          &.other {
            cursor: default;
          }

          .block {
            width: 35px;
            height: 35px;

            @media screen and (max-height: 860px) {
              width: 30px;
              height: 30px;
            }

            border-radius: 5px;
            margin-right: 0.5rem;
          }
        }
      }
    }

    .activity-notes {
      margin-top: $padding/8;
    }

    .graph-container {
      position: relative;
      padding-top: $padding * 2;
      padding-right: $padding * 2;
      background-color: $col-navy-dark;

      @media screen and (max-width: $mq-tablet) {
        padding-top: $padding;
        padding-right: $padding;
        flex: 1;
      }

      @media screen and (max-width: $mq-mobile) {
        padding-top: $padding/8;
        padding-right: 0;
      }
    }

    .tab {
      @include centered(true, true, column);
      width: 100%;
      height: $drawerHeight * 2;
      background-color: $col-white;
    }
  }

  /deep/ .container .btn-close {
    svg path {
      stroke: $col-white;
    }
  }
}
</style>
